/* ==========================================================================
   ASK A VET™ — Dog & Cat Pain Scale Calculator
   Shopify adaptation of client redesign dated 19 Aug 2026
   ========================================================================== */

(() => {
  'use strict';

  const ELEMENT_NAME = 's-pain-scale-calculator';
  if (customElements.get(ELEMENT_NAME)) return;

  function initialisePainScaleCalculator(calculatorRoot) {

      "use strict";

      /* ---------- DATA ---------- */

      // Dog: adapted from the Glasgow Composite Measure Pain Scale - Short Form (CMPS-SF).
      // Reference intervention point from the original clinician scale: 6/24 (or 5/20 if mobility is not assessed).
      var DOG = {
        max:24,
        maxNoMobility:20,
        threshold:6,
        thresholdNoMobility:5,
        er:[
          "Struggling to breathe, gasping, or gums are blue/white/grey",
          "Collapse, can't stand, or unresponsive",
          "A swollen, hard, bloated belly — especially with retching that brings nothing up",
          "Non-stop crying, screaming or panic that won't settle",
          "Been hit by a car, fallen, or any major trauma",
          "Uncontrolled bleeding, sudden paralysis or dragging limbs",
          "Repeatedly trying but unable to pass urine",
          "An active seizure lasting around five minutes, repeated or cluster seizures, or failure to recover normally afterwards"
        ],
        questions:[
          {
            key:"vocal",
            title:"Is your dog making pain sounds?",
            help:"Listen while they're resting or being left alone.",
            options:[
              {t:"Quiet — normal for them", d:"No unusual noise", v:0},
              {t:"Crying or whimpering", d:"Soft, on and off", v:1},
              {t:"Groaning", d:"Deep, low sounds", v:2},
              {t:"Screaming or yelping out", d:"Sharp, distressed", v:3}
            ]
          },
          {
            key:"attention",
            title:"How are they treating a sore spot?",
            help:"A wound, a joint, the belly, an ear — anywhere that seems tender. Pick the strongest thing you see.",
            options:[
              {t:"Ignoring it completely", d:"Leaving the area alone", v:0},
              {t:"Looking at it", d:"Turning to glance at the spot", v:1},
              {t:"Licking it", d:"Repeatedly licking the area", v:2},
              {t:"Rubbing it", d:"Rubbing on floor/furniture", v:3},
              {t:"Chewing or biting at it", d:"Going at the area with teeth", v:4},
              {t:"Cannot identify a painful area", d:"Choose this instead of guessing; the assessment will be marked incomplete", v:null}
            ]
          },
          {
            key:"mobility",
            title:"How are they moving?",
            help:"Watch them get up and walk a few steps only if safe. Choose ‘Refuses to move’ only when they are alert but unwilling because of pain. Inability to stand, collapse or profound weakness is an emergency.",
            skippable:true,
            options:[
              {t:"Normal", d:"Moving like their usual self", v:0},
              {t:"Limping", d:"Favouring a leg", v:1},
              {t:"Slow or reluctant", d:"Hesitant to get going", v:2},
              {t:"Stiff", d:"Tight, careful movement", v:3},
              {t:"Refuses to move because of pain", d:"Alert and responsive, but unwilling to get up", v:4},
              {t:"Cannot stand, collapsed or profoundly weak", d:"Emergency sign — stop and seek immediate veterinary care", v:"emergency", emergency:true}
            ]
          },
          {
            key:"touch",
            title:"How do they react to gentle touch near the sore area?",
            help:"Only if it is safe, touch gently beside — never directly on — the tender area. If you have already observed their response to gentle handling nearby, choose it. Otherwise select ‘Cannot assess safely’.",
            options:[
              {t:"Nothing — relaxed", d:"No reaction", v:0},
              {t:"Looks round at you", d:"Turns toward the touch", v:1},
              {t:"Flinches or pulls away", d:"Twitches from the area", v:2},
              {t:"Growls or guards it", d:"Warns you off / covers it", v:3},
              {t:"Snaps", d:"Tries to bite", v:4},
              {t:"Cries out", d:"Yelps from the touch", v:5},
              {t:"Cannot assess safely", d:"Do not touch them or guess; the assessment will be marked incomplete", v:null}
            ]
          },
          {
            key:"demeanour",
            title:"What's their overall mood?",
            help:"Think about how they're engaging with you and the world right now.",
            options:[
              {t:"Happy, content or bouncy", d:"Their normal self", v:0},
              {t:"Quiet", d:"Withdrawn but responsive", v:1},
              {t:"Indifferent", d:"Not interested in surroundings", v:2},
              {t:"Nervous, anxious or fearful", d:"On edge, jumpy", v:3},
              {t:"Depressed or very withdrawn", d:"Flat, but still responds to you", v:4},
              {t:"Unresponsive or collapsed", d:"Emergency sign — stop and seek immediate veterinary care", v:"emergency", emergency:true}
            ]
          },
          {
            key:"posture",
            title:"What's their body posture like?",
            help:"Look at how they hold and settle their body.",
            options:[
              {t:"Comfortable", d:"Relaxed, settles normally", v:0},
              {t:"Unsettled", d:"Can't quite get comfy", v:1},
              {t:"Restless", d:"Keeps shifting around", v:2},
              {t:"Hunched or tense", d:"Arched back, guarding belly", v:3},
              {t:"Rigid", d:"Stiff, frozen, braced", v:4}
            ]
          }
        ]
      };

      // Cat: the Feline Grimace Scale (FGS). Each action unit scored 0-2.
      // Score = sum / available maximum. Reference point from the original FGS: > 0.39.
      var CAT = {
        max:10,
        thresholdRatio:0.39,
        er:[
          "Straining in the litter tray with little or no urine — especially a male cat (this is a life-threatening emergency)",
          "Open-mouth breathing, panting, or fast/laboured breathing",
          "Collapse, hiding and unresponsive, or gums that are white/blue/grey",
          "Crying out non-stop or clear panic that won't settle",
          "Any major trauma — a fall, a car, a dog attack",
          "Uncontrolled bleeding, sudden paralysis or dragging limbs",
          "An active seizure lasting around five minutes, repeated or cluster seizures, or failure to recover normally afterwards"
        ],
        questions:[
          {
            key:"ears",
            title:"Where are their ears?",
            help:"Look at a calm cat face-on. Compare to how they normally look.",
            options:[
              {t:"Facing forward, normal", d:"Upright and relaxed", v:0},
              {t:"Slightly pulled apart / turned out", d:"A little flattened, rotated, or you are unsure", v:1},
              {t:"Clearly flattened and rotated outward", d:"Pinned apart, airplane ears", v:2},
              {t:"Cannot assess clearly", d:"The ears are hidden or not visible enough to score", v:null}
            ]
          },
          {
            key:"eyes",
            title:"How open are their eyes?",
            help:"Pain makes cats squint. Look at the gap between the eyelids.",
            options:[
              {t:"Open and normal", d:"Round, relaxed eyes", v:0},
              {t:"Partly squinted", d:"Narrowed, half-closed, or you are unsure", v:1},
              {t:"Tightly squinted or squeezed shut", d:"Barely open / clamped closed", v:2},
              {t:"Cannot assess clearly", d:"The eyes are hidden or not visible enough to score", v:null}
            ]
          },
          {
            key:"muzzle",
            title:"What shape is their muzzle?",
            help:"Look at the nose and mouth area — round and relaxed, or stretched tight?",
            options:[
              {t:"Relaxed and round", d:"Soft, normal shape", v:0},
              {t:"Slightly tense / oval", d:"Mild flattening, or you are unsure", v:1},
              {t:"Clearly tense, flat and stretched", d:"Elliptical, tight muzzle", v:2},
              {t:"Cannot assess clearly", d:"The muzzle is hidden or not visible enough to score", v:null}
            ]
          },
          {
            key:"whiskers",
            title:"What are the whiskers doing?",
            help:"Relaxed whiskers curve down and out to the side.",
            options:[
              {t:"Relaxed, curved to the side", d:"Loose and natural", v:0},
              {t:"Slightly straighter than normal", d:"Less curved, or you are unsure", v:1},
              {t:"Straight, stiff, pushed forward", d:"Standing out, tense", v:2},
              {t:"Cannot assess clearly", d:"The whiskers are hidden or not visible enough to score", v:null}
            ]
          },
          {
            key:"head",
            title:"How is their head held?",
            help:"Watch their resting head position relative to the shoulders.",
            options:[
              {t:"Up, above the shoulders", d:"Normal, alert carriage", v:0},
              {t:"Level with the shoulders", d:"Held lower than usual, or you are unsure", v:1},
              {t:"Below the shoulders or tilted down", d:"Hung low, tucked down or chin toward the chest", v:2},
              {t:"Cannot assess clearly", d:"The head position is hidden or not visible enough to score", v:null}
            ]
          }
        ]
      };

      /* ---------- STATE ---------- */
      var species = calculatorRoot.dataset.defaultSpecies === "cat" ? "cat" : "dog";
      var data = species === "dog" ? DOG : CAT;
      var idx = 0;
      var answers = {};        // key -> value
      var skipped = {};        // key -> true
      var lastResult = null;   // immutable snapshot used by print, email and PDF
      var activePdfUrls = [];
      var pdfRevokeTimers = [];
      var pdfBusy = false;
      var pdfBusyTimer = null;
      var printAncestors = [];

      /* ---------- ELEMENTS ---------- */
      var $ = function(id){return calculatorRoot.querySelector("#"+id);};
      function clearPrintLayout(){
        printAncestors.forEach(function(node){
          node.classList.remove("aav-pain-print-ancestor");
        });
        printAncestors=[];
      }
      function preparePrintLayout(){
        clearPrintLayout();
        var node=calculatorRoot.parentElement;
        while(node && node!==document.body){
          node.classList.add("aav-pain-print-ancestor");
          printAncestors.push(node);
          node=node.parentElement;
        }
      }
      function focusWithoutScroll(el){
        if(!el) return;
        try{el.focus({preventScroll:true});}catch(e){el.focus();}
      }
      function scrollToPanel(panel){
        var reduceMotion=window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if(panel && panel.scrollIntoView){
          panel.scrollIntoView({block:"start",behavior:reduceMotion?"auto":"smooth"});
        }else{
          window.scrollTo(0,0);
        }
      }
      function bindRadioKeyboard(group){
        group.addEventListener("keydown",function(event){
          var keys=["ArrowLeft","ArrowRight","ArrowUp","ArrowDown","Home","End"];
          if(keys.indexOf(event.key)===-1) return;
          var radios=Array.prototype.filter.call(group.querySelectorAll('[role="radio"]'),function(item){
            return !item.classList.contains("hidden") && !item.disabled;
          });
          var current=radios.indexOf(event.target);
          if(current===-1 || !radios.length) return;
          event.preventDefault();
          var next=current;
          if(event.key==="Home") next=0;
          else if(event.key==="End") next=radios.length-1;
          else if(event.key==="ArrowLeft" || event.key==="ArrowUp") next=(current-1+radios.length)%radios.length;
          else next=(current+1)%radios.length;
          radios[next].focus();
          radios[next].click();
        });
      }
      function updateRadioTabStops(group){
        var radios=Array.prototype.filter.call(group.querySelectorAll('[role="radio"]'),function(item){
          return !item.classList.contains("hidden") && !item.disabled;
        });
        var checked=null;
        radios.forEach(function(item){
          item.tabIndex=-1;
          if(item.getAttribute("aria-checked")==="true") checked=item;
        });
        if(checked) checked.tabIndex=0;
        else if(radios.length) radios[0].tabIndex=0;
      }
      function formatResultDate(date){
        try{
          return new Intl.DateTimeFormat("en-AU",{
            day:"numeric",month:"long",year:"numeric",
            hour:"numeric",minute:"2-digit",timeZoneName:"short"
          }).format(date);
        }catch(e){
          return date.toLocaleString();
        }
      }
      function fileDateStamp(date){
        function pad(n){return n<10?"0"+n:String(n);}
        return date.getFullYear()+"-"+pad(date.getMonth()+1)+"-"+pad(date.getDate());
      }
      function buildResponseSnapshot(){
        return data.questions.map(function(q){
          if(skipped[q.key]){
            return {
              question:q.title,
              answer:"Not assessed safely",
              detail:"Mobility was omitted from the scoring denominator.",
              points:null,
              status:"skipped"
            };
          }
          if(!answers.hasOwnProperty(q.key)){
            return {
              question:q.title,
              answer:"Not completed",
              detail:"The assessment stopped after an emergency sign was selected.",
              points:null,
              status:"not-completed"
            };
          }
          var value=answers[q.key];
          var option=null;
          q.options.some(function(o){
            if(o.v===value){option=o;return true;}
            return false;
          });
          return {
            question:q.title,
            answer:option?option.t:"No answer recorded",
            detail:option?option.d:"",
            points:typeof value==="number"?value:null,
            status:option && option.emergency?"emergency":(value===null?"not-assessable":"scored")
          };
        });
      }
      function freezeResultSnapshot(snapshot){
        snapshot.responses.forEach(Object.freeze);
        Object.freeze(snapshot.actions);
        Object.freeze(snapshot.emergencies);
        Object.freeze(snapshot.missingCategories);
        Object.freeze(snapshot.responses);
        return Object.freeze(snapshot);
      }
      function scoreDisplay(snapshot){
        if(snapshot.emergency) return "Emergency sign selected - screening score not used";
        if(snapshot.incomplete) return "Incomplete - no total calculated";
        var text=snapshot.scoreLabel+": "+snapshot.scoreText+" "+snapshot.ofText;
        if(snapshot.catRatio!==null) text+=" (normalised score "+snapshot.catRatio.toFixed(2)+")";
        return text;
      }
      function responsePointsLabel(response){
        if(response.status==="emergency") return "emergency sign; score not used";
        if(response.status==="not-completed") return "not reached after emergency warning";
        if(response.points===null) return "not scored";
        return response.points+" point"+(response.points===1?"":"s");
      }
      function populatePrintDetails(snapshot){
        $("printSpecies").textContent="Species: "+snapshot.speciesLabel;
        if(snapshot.assessedFeatures!==null){
          $("printFeatureCount").textContent="Facial features assessed: "+snapshot.assessedFeatures+" of 5"+
            (snapshot.missingCategories.length?". Not assessable: "+snapshot.missingCategories.join("; "):"");
        }else{
          $("printFeatureCount").textContent="";
        }
        $("printTimestamp").textContent="Generated "+snapshot.generatedLocal;
        var list=$("printResponseList");
        list.innerHTML="";
        snapshot.responses.forEach(function(response){
          var li=document.createElement("li");
          var points=responsePointsLabel(response);
          li.textContent=response.question+": "+response.answer+" ("+points+")."+
            (response.detail?" "+response.detail:"");
          list.appendChild(li);
        });
        $("printReference").textContent=snapshot.clinicalReferenceText+" Source: "+snapshot.clinicalUrl+" Calculator: "+snapshot.calculatorUrl;
      }
      function clearExportFeedback(){
        $("exportStatus").textContent="";
        $("exportError").textContent="";
      }
      function exportError(message){
        $("exportStatus").textContent="";
        $("exportError").textContent=message;
      }
      function revokeTrackedPdfUrl(url){
        if(url && window.URL && window.URL.revokeObjectURL) window.URL.revokeObjectURL(url);
        activePdfUrls=activePdfUrls.filter(function(activeUrl){return activeUrl!==url;});
      }
      function releasePdfUrl(){
        pdfRevokeTimers.forEach(function(timer){window.clearTimeout(timer);});
        pdfRevokeTimers=[];
        activePdfUrls.slice().forEach(revokeTrackedPdfUrl);
        if(pdfBusyTimer){
          window.clearTimeout(pdfBusyTimer);
          pdfBusyTimer=null;
        }
        pdfBusy=false;
      }
      var startEl=$("start"), quizEl=$("quiz"), resultEl=$("result");
      var startLead=$("startLead"), erList=$("erList");
      bindRadioKeyboard($("speciesSeg"));
      bindRadioKeyboard($("answerGroup"));

      /* ---------- START ---------- */
      var segButtons = calculatorRoot.querySelectorAll("#speciesSeg button");
      segButtons.forEach(function(button){
        var active=button.getAttribute("data-species")===species;
        button.classList.toggle("active",active);
        button.setAttribute("aria-checked",active?"true":"false");
      });
      updateRadioTabStops($("speciesSeg"));
      segButtons.forEach(function(b){
        b.addEventListener("click",function(){
          segButtons.forEach(function(x){x.classList.remove("active");});
          segButtons.forEach(function(x){x.setAttribute("aria-checked","false");});
          b.classList.add("active");
          b.setAttribute("aria-checked","true");
          updateRadioTabStops($("speciesSeg"));
          species=b.getAttribute("data-species");
          data = species==="dog"?DOG:CAT;
          renderStart();
        });
      });

      function renderStart(){
        if(species==="dog"){
          startLead.textContent=calculatorRoot.dataset.dogStartText || "This uses the same signs vets check in the clinic, translated into plain language for home use. Watch your dog calmly for a minute, then answer honestly.";
          $("beginBtn").textContent=calculatorRoot.dataset.dogButtonLabel || "Start dog assessment";
        }else{
          startLead.textContent=calculatorRoot.dataset.catStartText || "A cat's face can show subtle pain clues. Observe them awake and undisturbed for about 30 seconds. If they are sleeping, eating, grooming or vocalising, wait until they settle before scoring.";
          $("beginBtn").textContent=calculatorRoot.dataset.catButtonLabel || "Start cat assessment";
        }
        erList.textContent = "These examples override any score and are not exhaustive. Don't wait to finish the quiz — urgent red flags for a "+species+":";
        var wrap=erList.parentElement;
        var old=wrap.querySelector("ul"); if(old) old.remove();
        var ul=document.createElement("ul");
        data.er.forEach(function(t){var li=document.createElement("li");li.textContent=t;ul.appendChild(li);});
        wrap.appendChild(ul);
      }

      $("beginBtn").addEventListener("click",function(){
        idx=0;answers={};skipped={};lastResult=null;
        releasePdfUrl();
        clearExportFeedback();
        document.body.classList.remove("has-aav-pain-result");
        startEl.classList.add("hidden");
        resultEl.classList.add("hidden");
        quizEl.classList.remove("hidden");
        renderQuestion();
        scrollToPanel(quizEl);
        focusWithoutScroll($("qTitle"));
      });

      /* ---------- QUIZ ---------- */
      function renderQuestion(){
        var qs=data.questions;
        var q=qs[idx];
        $("stepLabel").textContent="Question "+(idx+1);
        $("stepCount").textContent=(idx+1)+" / "+qs.length;
        var completed=qs.reduce(function(total,item){
          return total+((answers.hasOwnProperty(item.key) || skipped[item.key])?1:0);
        },0);
        var progress=Math.round(completed/qs.length*100);
        $("barFill").style.width=progress+"%";
        $("progressBar").setAttribute("aria-valuenow",progress);
        $("progressBar").setAttribute("aria-valuetext",completed+" of "+qs.length+" questions answered");
        $("qTitle").textContent=q.title;
        $("qHelp").textContent=q.help;

        var box=$("opts");box.innerHTML="";
        q.options.forEach(function(o){
          var btn=document.createElement("button");
          btn.type="button";
          btn.setAttribute("role","radio");
          btn.className="opt"+(o.emergency?" emergency-option":"")+(answers[q.key]===o.v && !skipped[q.key]?" selected":"");
          btn.setAttribute("aria-checked",answers[q.key]===o.v && !skipped[q.key]?"true":"false");
          btn.innerHTML='<span class="mark"></span><span class="txt"><b>'+o.t+'</b><span>'+o.d+'</span></span>';
          btn.addEventListener("click",function(){
            answers[q.key]=o.v;
            delete skipped[q.key];
            renderQuestion();
            focusWithoutScroll($("opts").querySelector('[aria-checked="true"]'));
          });
          box.appendChild(btn);
        });

        var skipBtn=$("skipBtn");
        if(q.skippable){
          skipBtn.classList.remove("hidden");
          skipBtn.classList.toggle("selected",!!skipped[q.key]);
          skipBtn.setAttribute("aria-checked",skipped[q.key]?"true":"false");
        }else{
          skipBtn.classList.add("hidden");
          skipBtn.classList.remove("selected");
          skipBtn.setAttribute("aria-checked","false");
        }
        updateRadioTabStops($("answerGroup"));

        var answered = (answers.hasOwnProperty(q.key)) || skipped[q.key];
        var emergencyAnswer=q.options.some(function(option){
          return option.emergency && answers[q.key]===option.v;
        });
        $("nextBtn").disabled=!answered;
        $("nextBtn").classList.toggle("btn-emergency",emergencyAnswer);
        $("nextBtn").textContent = emergencyAnswer ? "Show emergency guidance" : (idx===qs.length-1 ? (calculatorRoot.dataset.resultButtonLabel || "See result") : (calculatorRoot.dataset.nextButtonLabel || "Next"));
      }

      $("skipBtn").addEventListener("click",function(){
        var q=data.questions[idx];
        skipped[q.key]=true;
        delete answers[q.key];
        renderQuestion();
        focusWithoutScroll($("skipBtn"));
      });

      $("nextBtn").addEventListener("click",function(){
        var currentQuestion=data.questions[idx];
        var emergencyAnswer=currentQuestion.options.some(function(option){
          return option.emergency && answers[currentQuestion.key]===option.v;
        });
        if(emergencyAnswer){
          showResult();
        }else if(idx<data.questions.length-1){
          idx++;renderQuestion();scrollToPanel(quizEl);
          focusWithoutScroll($("qTitle"));
        }else{
          showResult();
        }
      });
      $("backBtn").addEventListener("click",function(){
        if(idx>0){idx--;renderQuestion();scrollToPanel(quizEl);focusWithoutScroll($("qTitle"));}
        else{quizEl.classList.add("hidden");startEl.classList.remove("hidden");scrollToPanel(startEl);focusWithoutScroll($("beginBtn"));}
      });

      /* ---------- RESULT ---------- */
      function showResult(){
        var sum=0;
        var band;
        var availableMax=0;
        var scoreMax=null;
        var catRatio=null;
        var assessedFeatures=null;
        var missingCategories=[];
        var emergencySelections=[];
        data.questions.forEach(function(q){
          q.options.forEach(function(option){
            if(option.emergency && answers[q.key]===option.v) emergencySelections.push(option.t);
          });
        });
        if(emergencySelections.length){
          band=emergencyBand(emergencySelections);
          band.scoreText="!";
          band.ofText="Emergency";
        }else if(species==="dog"){
          var missingDog=[];
          DOG.questions.forEach(function(q){
            if(!skipped[q.key] && typeof answers[q.key]==="number") sum+=answers[q.key];
            if(answers[q.key]===null && q.key==="attention") missingDog.push("painful area");
            if(answers[q.key]===null && q.key==="touch") missingDog.push("touch response");
          });
          if(missingDog.length){
            band=dogIncomplete(missingDog);
            band.scoreText="—";
            band.ofText="Incomplete";
            missingCategories=missingDog.slice();
          }else{
            var mobilitySkipped = skipped["mobility"];
            var max = mobilitySkipped ? DOG.maxNoMobility : DOG.max;
            var thr = mobilitySkipped ? DOG.thresholdNoMobility : DOG.threshold;
            band = dogBand(sum,max,thr);
            band.scoreText = sum;
            band.ofText = "/ "+max;
            scoreMax=max;
          }
        }else{
          CAT.questions.forEach(function(q){
            if(typeof answers[q.key]==="number"){
              sum+=answers[q.key];
              availableMax+=2;
            }else{
              missingCategories.push(q.title);
            }
          });
          band = catBand(sum,availableMax);
          band.scoreText = availableMax>=8 ? sum : "—";
          band.ofText = availableMax>=8 ? "/ "+availableMax : "Cannot score";
          assessedFeatures=availableMax/2;
          if(availableMax>=8){
            scoreMax=availableMax;
            catRatio=sum/availableMax;
          }
        }

        // paint
        $("scoreLabel").textContent=band.emergency?"Emergency warning":(band.incomplete?"Assessment incomplete":(species==="dog"?"Adapted home screening total":"Feline facial screening total"));
        $("scoreBig").textContent=band.scoreText;
        $("scoreOf").textContent=band.ofText;
        var resultCard=$("resultCard");
        ["tone-low","tone-review","tone-incomplete","tone-emergency"].forEach(function(tone){
          resultCard.classList.remove(tone);
        });
        resultCard.classList.add("tone-"+band.tone);
        var badge=$("badge");
        badge.textContent=band.label;
        $("resultTitle").textContent=band.title;
        $("resultSummary").textContent=band.summary;
        var spokenScore=(band.incomplete || band.emergency)?"":"Score "+band.scoreText+" "+band.ofText.replace("/ ","out of ")+". ";
        $("resultA11ySummary").textContent=spokenScore+band.label+". "+band.summary;

        var ul=$("actionList");ul.innerHTML="";
        band.actions.forEach(function(a){var li=document.createElement("li");li.textContent=a;ul.appendChild(li);});

        var erul=$("resultERlist");erul.innerHTML="";
        data.er.forEach(function(t){var li=document.createElement("li");li.textContent=t;erul.appendChild(li);});

        var disclaimerText="This owner-facing calculator adapts categories from the "+(species==="dog"?"Glasgow Composite Measure Pain Scale (Short Form)":"Feline Grimace Scale")+" using simplified wording. The original tools are validated for clinical use; this home adaptation is an educational screening guide, not a validated clinical pain score or a replacement for a veterinary examination. A low score does not rule pain out. This result reflects observations at the recorded time only; your pet's condition may change. If you are worried, contact your vet.";
        $("disclaimer").textContent=disclaimerText;

        var generatedAt=new Date();
        var responseSnapshot=buildResponseSnapshot();
        lastResult=freezeResultSnapshot({
          species:species,
          speciesLabel:species==="dog"?"Dog":"Cat",
          emergency:!!band.emergency,
          incomplete:!!band.incomplete,
          missingCategories:missingCategories.slice(),
          scoreLabel:$("scoreLabel").textContent,
          scoreText:band.scoreText,
          ofText:band.ofText,
          rawScore:(band.incomplete || band.emergency)?null:sum,
          scoreMax:scoreMax,
          catRatio:catRatio,
          assessedFeatures:assessedFeatures,
          classification:band.label,
          title:band.title,
          summary:band.summary,
          actions:band.actions.slice(),
          emergencies:data.er.slice(),
          responses:responseSnapshot,
          disclaimer:disclaimerText,
          generatedLocal:formatResultDate(generatedAt),
          generatedIso:generatedAt.toISOString(),
          fileStamp:fileDateStamp(generatedAt),
          calculatorUrl:calculatorRoot.dataset.shareUrl || window.location.href,
          clinicalReference:species==="dog"?"Glasgow Composite Measure Pain Scale Short Form (CMPS-SF)":"Feline Grimace Scale (FGS)",
          clinicalReferenceText:species==="dog"?
            "Original clinician-administered CMPS-SF reference point: 6/24, or 5/20 when mobility is omitted.":
            "Original Feline Grimace Scale reference point: normalised score above 0.39.",
          clinicalUrl:species==="dog"?
            "https://doi.org/10.1017/S096272860003178X":
            "https://doi.org/10.1038/s41598-019-55693-8"
        });
        populatePrintDetails(lastResult);
        clearExportFeedback();
        $("pdfBtn").disabled=false;
        $("pdfBtn").setAttribute("aria-busy","false");

        quizEl.classList.add("hidden");
        resultEl.classList.remove("hidden");
        document.body.classList.add("has-aav-pain-result");
        scrollToPanel(resultEl);
        focusWithoutScroll(resultEl);
      }

      function emergencyBand(signs){
        return {
          emergency:true,
          tone:"emergency",
          label:"Emergency sign selected",
          title:"Stop and seek emergency veterinary care now",
          summary:"You selected: "+signs.join("; ")+". This can signal a life-threatening problem, so the screening score is not used and must not delay urgent in-person care.",
          actions:[
            "Stop the assessment and go to the nearest emergency veterinary clinic now.",
            "Phone the clinic while travelling if someone else can call safely, so the team can prepare.",
            "Keep your pet as still, quiet and safely supported as possible. Do not force them to stand or walk.",
            "Do not give food, human medicine or leftover medication unless the emergency veterinarian specifically directs you."
          ]
        };
      }

      function dogIncomplete(missing){
        return {
          incomplete:true,
          tone:"incomplete",
          label:"Incomplete assessment",
          title:"A safe screening result cannot be calculated",
          summary:"The "+missing.join(" and ")+" could not be assessed. A partial total should not be compared with the 6/24 or 5/20 reference point from the original clinical scale.",
          actions:[
            "Do not touch, move or provoke your dog just to finish the calculator.",
            "Arrange a same-day veterinary assessment if you suspect pain or their behaviour is abnormal.",
            "Go to an emergency clinic now if pain is severe or rapidly worsening, they cannot settle, or any red flag below is present.",
            "Do not give human or leftover medication unless a veterinarian who knows your pet specifically directs you. If any has already been given, contact a vet or animal poison service now."
          ]
        };
      }

      function dogBand(sum,max,thr){
        // thr = reference point from the original clinician-administered scale.
        if(sum < thr){
          return {
            tone:"low",
            label:"Below reference point",
            title:"Lower pain-screening score",
            summary:"This adapted home total is numerically below the reference point used by the original clinician-administered scale. It does not show that your dog is pain-free, cannot rule out pain and must not determine whether pain medication is needed.",
            actions:[
              "Keep them quiet, comfortably positioned and rested — avoid big exercise, stairs or jumping.",
              "Re-check in a few hours, or sooner if the signs change or worsen.",
              "Contact your vet if pain signs persist, your dog is unwell or you remain concerned.",
              "Do not give human or leftover medication unless a veterinarian who knows your pet specifically directs you. If any has already been given, contact a vet or animal poison service now."
            ]
          };
        }
        return {
          tone:"review",
          label:"At or above reference point",
          title:"Veterinary assessment is recommended",
          summary:"This adapted home total is numerically at or above the reference point used by the original clinician-administered scale. The home total is not validated and must not determine treatment. A veterinarian should assess the cause and decide what pain relief or other care is appropriate.",
          actions:[
            "Arrange a same-day veterinary assessment and describe the signs you are seeing.",
            "Go to an emergency clinic now if pain is severe or rapidly worsening, they cannot settle, or any red flag below is present.",
            "Keep them confined and comfortable — limit movement to avoid making it worse.",
            "Do not give human or leftover medication unless a veterinarian who knows your pet specifically directs you. If any has already been given, contact a vet or animal poison service now."
          ]
        };
      }

      function catBand(sum,max){
        // FGS score = sum / available maximum. Reference point from the original FGS is >0.39.
        if(max<8){
          return {
            incomplete:true,
            tone:"incomplete",
            label:"Unable to calculate",
            title:"Not enough facial features were visible",
            summary:"At least four of the five facial features need to be visible for a useful home screening result. This does not mean your cat is comfortable or pain-free.",
            actions:[
              "Let your cat settle somewhere quiet, then look again without restraining them.",
              "Try a clear front-facing photo if it is safe and does not cause stress.",
              "Arrange a same-day veterinary assessment if your cat is unwell, behaving abnormally or you remain concerned.",
              "Do not give human or leftover medication unless a veterinarian who knows your pet specifically directs you. If any has already been given, contact a vet or animal poison service now."
            ]
          };
        }
        var ratio=sum/max;
        if(ratio<=CAT.thresholdRatio){
          return {
            tone:"low",
            label:"Below reference point",
            title:"Lower facial pain-screening score",
            summary:"The assessable facial features give a normalised home screening calculation of "+ratio.toFixed(2)+", numerically below the reference point reported for the original Feline Grimace Scale. This adaptation cannot rule out pain or determine treatment, so keep watching closely.",
            actions:[
              "Give them a quiet, comfortable hiding spot with minimal disturbance.",
              "Watch eating, drinking, grooming and litter tray use — changes are early warnings.",
              "Re-check in a few hours, or sooner if the signs change or worsen.",
              "Contact your vet if pain signs persist, your cat is unwell or you remain concerned.",
              "Do not give human or leftover medication unless a veterinarian who knows your pet specifically directs you. If any has already been given, contact a vet or animal poison service now."
            ]
          };
        }
        return {
          tone:"review",
          label:"Above reference point",
          title:"Veterinary assessment is recommended",
          summary:"The assessable facial features give a normalised home screening calculation of "+ratio.toFixed(2)+", numerically above the reference point reported for the original Feline Grimace Scale. This adaptation is not validated to determine treatment. A veterinarian should assess the cause and decide what care is appropriate.",
          actions:[
            "Arrange a same-day veterinary assessment and describe the facial signs and any behaviour changes.",
            "Go to an emergency clinic now if pain is severe or rapidly worsening, they cannot settle, or any red flag below is present.",
            "Keep them calm, comfortable and undisturbed — do not force them out of hiding.",
            "Do not give human or leftover medication unless a veterinarian who knows your pet specifically directs you. If any has already been given, contact a vet or animal poison service now."
          ]
        };
      }

      /* ---------- RESULT EXPORTS ---------- */
      function normalisePdfText(value){
        var text=String(value===null || value===undefined?"":value)
          .replace(/\u00a0/g," ")
          .replace(/\u2018|\u2019|\u201a|\u201b/g,"'")
          .replace(/\u201c|\u201d|\u201e|\u201f/g,'"')
          .replace(/\u2013|\u2014|\u2212/g,"-")
          .replace(/\u2026/g,"...")
          .replace(/\u2122/g,"(TM)")
          .replace(/\r|\n|\t/g," ");
        if(text.normalize){
          text=text.normalize("NFD").replace(/[\u0300-\u036f]/g,"");
        }
        return text.replace(/[^\x20-\x7e]/g,"").replace(/\s+/g," ").trim();
      }
      function pdfEscape(value){
        return normalisePdfText(value)
          .replace(/\\/g,"\\\\")
          .replace(/\(/g,"\\(")
          .replace(/\)/g,"\\)");
      }
      function pdfTextWidth(text,size){
        var units=0;
        normalisePdfText(text).split("").forEach(function(ch){
          if(ch===" ") units+=0.28;
          else if(/[ilI.,'!:;]/.test(ch)) units+=0.27;
          else if(/[mwMW@%&]/.test(ch)) units+=0.82;
          else if(/[A-Z]/.test(ch)) units+=0.64;
          else if(/[0-9]/.test(ch)) units+=0.56;
          else units+=0.50;
        });
        return units*size;
      }
      function wrapPdfText(value,size,maxWidth){
        var clean=normalisePdfText(value);
        if(!clean) return [""];
        var safeWidth=Math.max(20,maxWidth-12);
        var words=clean.split(" ");
        var lines=[];
        var line="";
        words.forEach(function(word){
          var pieces=[];
          if(pdfTextWidth(word,size)<=safeWidth){
            pieces=[word];
          }else{
            var piece="";
            word.split("").forEach(function(ch){
              if(piece && pdfTextWidth(piece+ch,size)>safeWidth){
                pieces.push(piece);
                piece=ch;
              }else{
                piece+=ch;
              }
            });
            if(piece) pieces.push(piece);
          }
          pieces.forEach(function(part){
            var candidate=line?line+" "+part:part;
            if(line && pdfTextWidth(candidate,size)>safeWidth){
              lines.push(line);
              line=part;
            }else{
              line=candidate;
            }
          });
        });
        if(line) lines.push(line);
        return lines;
      }
      function pdfEncode(text){
        var bytes=new Uint8Array(text.length);
        for(var i=0;i<text.length;i++) bytes[i]=text.charCodeAt(i)&255;
        return bytes;
      }
      function pdfDate(iso){
        var d=new Date(iso);
        function two(n){return n<10?"0"+n:String(n);}
        return "D:"+d.getUTCFullYear()+two(d.getUTCMonth()+1)+two(d.getUTCDate())+
          two(d.getUTCHours())+two(d.getUTCMinutes())+two(d.getUTCSeconds())+"Z";
      }
      function buildResultPdf(snapshot){
        var pageWidth=595;
        var pageHeight=842;
        var margin=48;
        var contentWidth=pageWidth-(margin*2);
        var contentTop=754;
        var contentBottom=58;
        var pages=[];
        var page;
        var y;

        function newPage(){
          page=[];
          pages.push(page);
          y=contentTop;
        }
        function ensureSpace(height){
          if(y-height<contentBottom) newPage();
        }
        function addLine(text,options){
          options=options||{};
          var size=options.size||10.5;
          var lineHeight=options.lineHeight||size*1.35;
          ensureSpace(lineHeight);
          page.push({
            text:normalisePdfText(text),
            x:options.x===undefined?margin:options.x,
            y:y,
            size:size,
            bold:!!options.bold,
            colour:options.colour||"0.063 0.067 0.137"
          });
          y-=lineHeight;
        }
        function addParagraph(text,options){
          options=options||{};
          var size=options.size||10.5;
          var indent=options.indent||0;
          var bullet=!!options.bullet;
          var bulletSpace=bullet?13:0;
          var lines=wrapPdfText(text,size,contentWidth-indent-bulletSpace);
          lines.forEach(function(line,index){
            ensureSpace((options.lineHeight||size*1.35));
            if(bullet && index===0){
              page.push({
                text:"-",
                x:margin+indent,
                y:y,
                size:size,
                bold:true,
                colour:options.colour||"0.063 0.067 0.137"
              });
            }
            addLine(line,{
              size:size,
              lineHeight:options.lineHeight,
              bold:options.bold,
              colour:options.colour,
              x:margin+indent+bulletSpace
            });
          });
          y-=options.gapAfter===undefined?5:options.gapAfter;
        }
        function addSection(title,entries,bullets){
          y-=5;
          var titleLines=wrapPdfText(title,13,contentWidth);
          var firstLineHeight=entries.length?14:0;
          ensureSpace(titleLines.length*17+firstLineHeight+4);
          addParagraph(title,{size:13,bold:true,colour:"0.427 0.165 0.580",gapAfter:4});
          entries.forEach(function(entry){
            addParagraph(entry,{bullet:!!bullets,indent:bullets?2:0,gapAfter:bullets?3:5});
          });
        }

        newPage();
        addParagraph("ASK A VET(TM) adapted home pain screening result",{
          size:18,bold:true,colour:"0.024 0.055 0.373",gapAfter:9
        });
        addParagraph("Generated "+snapshot.generatedLocal,{size:9.5,colour:"0.278 0.251 0.388",gapAfter:10});

        var summaryLines=[
          "Species: "+snapshot.speciesLabel,
          "Result: "+scoreDisplay(snapshot),
          "Screening category: "+snapshot.classification,
          snapshot.title,
          snapshot.summary
        ];
        if(snapshot.assessedFeatures!==null){
          summaryLines.splice(2,0,"Facial features assessed: "+snapshot.assessedFeatures+" of 5");
        }
        if(snapshot.missingCategories.length){
          summaryLines.splice(2,0,"Not assessable: "+snapshot.missingCategories.join("; "));
        }
        addSection("Assessment summary",summaryLines,false);

        var responseLines=snapshot.responses.map(function(response){
          var points=responsePointsLabel(response);
          var text=response.question+": "+response.answer+" ("+points+")";
          if(response.detail) text+=". "+response.detail;
          return text;
        });
        addSection("Assessment responses",responseLines,true);
        addSection("What to do next",snapshot.actions,true);

        var emergencyLines=[
          "These examples are not exhaustive and override any score. Seek immediate in-person veterinary care if any is present."
        ].concat(snapshot.emergencies);
        addSection("Emergency signs",emergencyLines,true);
        addSection("Important limitations",[snapshot.disclaimer],false);
        addSection("Clinical reference",[
          snapshot.clinicalReferenceText,
          snapshot.clinicalReference+": "+snapshot.clinicalUrl,
          "Calculator: "+snapshot.calculatorUrl
        ],false);

        function textCommand(line){
          return "BT\n/"+(line.bold?"F2":"F1")+" "+line.size.toFixed(1)+" Tf\n"+
            line.colour+" rg\n1 0 0 1 "+line.x.toFixed(1)+" "+line.y.toFixed(1)+" Tm\n"+
            "("+pdfEscape(line.text)+") Tj\nET\n";
        }
        var streams=pages.map(function(lines,pageIndex){
          var stream="";
          stream+="BT\n/F2 15 Tf\n0.518 0.216 0.698 rg\n1 0 0 1 48 807 Tm\n(ASK A VET\\(TM\\)) Tj\nET\n";
          stream+="BT\n/F1 9 Tf\n0.278 0.251 0.388 rg\n1 0 0 1 48 792 Tm\n(Dog & Cat Pain Scale Calculator) Tj\nET\n";
          stream+="q\n0.518 0.216 0.698 RG\n2 w\n48 782 m 547 782 l S\nQ\n";
          lines.forEach(function(line){stream+=textCommand(line);});
          stream+="q\n0.898 0.882 0.937 RG\n0.8 w\n48 45 m 547 45 l S\nQ\n";
          stream+="BT\n/F1 8 Tf\n0.278 0.251 0.388 rg\n1 0 0 1 48 29 Tm\n(Educational screening guide - not a diagnosis) Tj\nET\n";
          stream+="BT\n/F1 8 Tf\n0.278 0.251 0.388 rg\n1 0 0 1 492 29 Tm\n(Page "+(pageIndex+1)+" of "+pages.length+") Tj\nET\n";
          return stream;
        });

        var objects=[];
        var pageRefs=[];
        streams.forEach(function(stream,index){
          pageRefs.push((6+index*2)+" 0 R");
        });
        objects[1]="<< /Type /Catalog /Pages 2 0 R /Lang (en-AU) >>";
        objects[2]="<< /Type /Pages /Kids ["+pageRefs.join(" ")+"] /Count "+streams.length+" >>";
        objects[3]="<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>";
        objects[4]="<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>";
        objects[5]="<< /Title ("+pdfEscape("ASK A VET(TM) "+snapshot.speciesLabel+" pain screening result")+
          ") /Author ("+pdfEscape("Dr Duncan Houston, BVSc - ASK A VET(TM)")+
          ") /Subject ("+pdfEscape("Adapted home pain screening result")+
          ") /Creator (ASK A VET\\(TM\\) Pain Scale Calculator) /CreationDate ("+pdfDate(snapshot.generatedIso)+") >>";
        streams.forEach(function(stream,index){
          var pageObject=6+index*2;
          var contentObject=pageObject+1;
          objects[pageObject]="<< /Type /Page /Parent 2 0 R /MediaBox [0 0 "+pageWidth+" "+pageHeight+
            "] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents "+contentObject+" 0 R >>";
          objects[contentObject]="<< /Length "+pdfEncode(stream).length+" >>\nstream\n"+stream+"endstream";
        });

        var totalObjects=objects.length-1;
        var pdf="%PDF-1.4\n";
        var offsets=[0];
        for(var objectNumber=1;objectNumber<=totalObjects;objectNumber++){
          offsets[objectNumber]=pdfEncode(pdf).length;
          pdf+=objectNumber+" 0 obj\n"+objects[objectNumber]+"\nendobj\n";
        }
        var xrefOffset=pdfEncode(pdf).length;
        pdf+="xref\n0 "+(totalObjects+1)+"\n";
        pdf+="0000000000 65535 f \n";
        for(var offsetIndex=1;offsetIndex<=totalObjects;offsetIndex++){
          var offsetText=String(offsets[offsetIndex]);
          while(offsetText.length<10) offsetText="0"+offsetText;
          pdf+=offsetText+" 00000 n \n";
        }
        pdf+="trailer\n<< /Size "+(totalObjects+1)+" /Root 1 0 R /Info 5 0 R >>\n";
        pdf+="startxref\n"+xrefOffset+"\n%%EOF\n";
        return pdfEncode(pdf);
      }
      function buildEmailBody(snapshot){
        var lines=[
          "ASK A VET(TM) brief pain screening summary",
          "Generated: "+snapshot.generatedLocal,
          "Species: "+snapshot.speciesLabel,
          "Result: "+scoreDisplay(snapshot)
        ];
        if(snapshot.assessedFeatures!==null) lines.push("Features assessed: "+snapshot.assessedFeatures+" of 5");
        if(snapshot.incomplete && snapshot.missingCategories.length){
          var compactCategory={
            "Where are their ears?":"ears",
            "How open are their eyes?":"eyes",
            "What shape is their muzzle?":"muzzle",
            "What are the whiskers doing?":"whiskers",
            "How is their head held?":"head position"
          };
          lines.push("Not assessable: "+snapshot.missingCategories.map(function(category){
            return compactCategory[category]||category;
          }).join(", "));
        }
        lines.push(
          "",
          snapshot.title,
          snapshot.summary,
          "",
          "URGENT: Go to an emergency vet for breathing trouble, collapse, major trauma or bleeding, prolonged or repeated seizures, paralysis, inability to pass urine, a hard swollen abdomen or unrelenting distress.",
          "Educational guide only. A low result does not rule out pain.",
          ""
        );
        var notAssessed=[];
        snapshot.responses.forEach(function(response){
          if(response.status==="skipped" || response.status==="not-assessable"){
            var observation="- "+response.question+": "+response.answer;
            if(response.detail) observation+=". "+response.detail;
            notAssessed.push(observation);
          }
        });
        if(notAssessed.length && !snapshot.missingCategories.length) lines.push("Items not assessed:",notAssessed.join("\r\n"),"");
        var keyActions=[];
        function addKeyAction(action){
          if(action && keyActions.indexOf(action)===-1) keyActions.push(action);
        }
        snapshot.actions.forEach(function(action){
          if(!keyActions.length && /stop the assessment|contact your vet|veterinary assessment|emergency clinic/i.test(action)) addKeyAction(action);
        });
        if(!keyActions.length) addKeyAction(snapshot.actions[0]);
        addKeyAction(snapshot.actions[snapshot.actions.length-1]);
        lines.push("Key next steps:");
        keyActions.slice(0,2).forEach(function(action){lines.push("- "+action);});
        lines.push("","The PDF contains every answer and full guidance.");
        lines.push("Calculator: "+snapshot.calculatorUrl);
        return lines.join("\r\n");
      }
      function downloadResultPdf(){
        if(!lastResult){
          exportError("Please complete an assessment before downloading a PDF.");
          return;
        }
        if(pdfBusy) return;
        pdfBusy=true;
        var button=$("pdfBtn");
        var restorePdfFocus=document.activeElement===button;
        clearExportFeedback();
        button.disabled=true;
        button.setAttribute("aria-busy","true");
        $("exportStatus").textContent="Preparing your PDF...";
        try{
          if(!window.Blob || !window.URL || !window.URL.createObjectURL){
            throw new Error("PDF downloads are not supported by this browser.");
          }
          var bytes=buildResultPdf(lastResult);
          var blob=new window.Blob([bytes],{type:"application/pdf"});
          var pdfUrl=window.URL.createObjectURL(blob);
          activePdfUrls.push(pdfUrl);
          var link=document.createElement("a");
          link.href=pdfUrl;
          var filename="ask-a-vet-"+lastResult.species+"-pain-screening-"+lastResult.fileStamp+".pdf";
          var isIOS=/iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform==="MacIntel" && navigator.maxTouchPoints>1);
          var opensInTab=isIOS || !("download" in link);
          if(opensInTab){
            link.target="_blank";
            link.rel="noopener";
          }else{
            link.download=filename;
          }
          document.body.appendChild(link);
          link.click();
          if(link.remove) link.remove();
          else document.body.removeChild(link);
          $("exportStatus").textContent=opensInTab?"Your PDF opened in a new tab.":"Your PDF download has started.";
          var revokeTimer=window.setTimeout(function(){
            revokeTrackedPdfUrl(pdfUrl);
            pdfRevokeTimers=pdfRevokeTimers.filter(function(timer){return timer!==revokeTimer;});
          },60000);
          pdfRevokeTimers.push(revokeTimer);
        }catch(error){
          exportError(error && error.message?error.message:"The PDF could not be created. Please use Print result instead.");
        }finally{
          pdfBusyTimer=window.setTimeout(function(){
            pdfBusy=false;
            pdfBusyTimer=null;
            button.disabled=false;
            button.setAttribute("aria-busy","false");
            var activeElement=document.activeElement;
            if(restorePdfFocus && (!activeElement || activeElement===document.body || activeElement===button)){
              focusWithoutScroll(button);
            }
          },750);
        }
      }
      function printResult(){
        if(!lastResult){
          exportError("Please complete an assessment before printing.");
          return;
        }
        clearExportFeedback();
        $("exportStatus").textContent="Opening the print dialog...";
        try{
          if(typeof window.print!=="function") throw new Error("Printing is not available in this browser.");
          preparePrintLayout();
          window.print();
        }catch(error){
          clearPrintLayout();
          exportError(error && error.message?error.message:"The print dialog could not be opened.");
        }
      }
      function emailResult(){
        if(!lastResult){
          exportError("Please complete an assessment before creating an email.");
          return;
        }
        clearExportFeedback();
        var subject=lastResult.speciesLabel+" pain screening result - ASK A VET";
        var href="mailto:?subject="+encodeURIComponent(subject)+"&body="+encodeURIComponent(buildEmailBody(lastResult));
        if(href.length>1900){
          exportError("This summary is too long for a reliable email link. Please download the PDF and attach it to your email.");
          return;
        }
        $("exportStatus").textContent="Opening your email app...";
        window.location.href=href;
      }
      function resetAssessment(){
        idx=0;
        answers={};
        skipped={};
        lastResult=null;
        releasePdfUrl();
        clearPrintLayout();
        clearExportFeedback();
        $("printSpecies").textContent="";
        $("printFeatureCount").textContent="";
        $("printTimestamp").textContent="";
        $("printResponseList").innerHTML="";
        $("printReference").textContent="";
        $("scoreLabel").textContent="Adapted home screening total";
        $("scoreBig").textContent="";
        $("scoreOf").textContent="";
        $("badge").textContent="";
        $("resultTitle").textContent="Pet pain assessment result";
        $("resultSummary").textContent="";
        $("actionList").innerHTML="";
        $("resultERlist").innerHTML="";
        $("disclaimer").textContent="";
        $("resultA11ySummary").textContent="Complete the assessment to hear a result summary.";
        ["tone-low","tone-review","tone-incomplete","tone-emergency"].forEach(function(tone){
          $("resultCard").classList.remove(tone);
        });
        resultEl.classList.add("hidden");
        quizEl.classList.add("hidden");
        startEl.classList.remove("hidden");
        document.body.classList.remove("has-aav-pain-result");
        renderStart();
        updateRadioTabStops($("speciesSeg"));
        scrollToPanel(startEl);
        focusWithoutScroll($("speciesSeg").querySelector('[aria-checked="true"]')||$("beginBtn"));
      }

      $("pdfBtn").addEventListener("click",downloadResultPdf);
      $("printBtn").addEventListener("click",printResult);
      $("emailBtn").addEventListener("click",emailResult);
      $("restartBtn").addEventListener("click",resetAssessment);
      window.addEventListener("beforeprint",function(){
        if(lastResult) preparePrintLayout();
      });
      window.addEventListener("afterprint",function(){
        clearPrintLayout();
        if(lastResult) $("exportStatus").textContent="";
      });

      /* init */
      updateRadioTabStops($("speciesSeg"));
      renderStart();
  }

  class PainScaleCalculator extends HTMLElement {
    connectedCallback() {
      if (this.__painScaleInitialised) return;
      this.__painScaleInitialised = true;
      initialisePainScaleCalculator(this);
    }
  }

  customElements.define(ELEMENT_NAME, PainScaleCalculator);
})();
